import { useTranslation } from "react-i18next"
import { Header } from "@/components/layout/header"
import { Footer } from "@/components/layout/footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Link } from "react-router-dom"
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Lock,
  Shield,
  Scale,
  Globe,
  Truck,
  Package,
  Search,
  FileText,
  Users,
  CreditCard,
  BarChart3,
  Bell,
  Star,
  MapPin,
  MessageSquare,
  ClipboardList,
  Zap,
  Ship,
  Building2,
  Flame,
  Heart,
  Leaf,
  Cpu,
  ShoppingBag,
  Mountain,
  Car,
  GraduationCap,
  BookOpen,
  TrendingUp,
} from "lucide-react"

// ─── Data ────────────────────────────────────────────────────────────────────

const workflowSteps = [
  { icon: FileText,    key: "step1", color: "bg-blue-500/10   text-blue-500"   },
  { icon: Users,       key: "step2", color: "bg-violet-500/10 text-violet-500" },
  { icon: MessageSquare, key: "step3", color: "bg-cyan-500/10 text-cyan-500"   },
  { icon: Scale,       key: "step4", color: "bg-amber-500/10  text-amber-500"  },
  { icon: MapPin,      key: "step5", color: "bg-green-500/10  text-green-500"  },
  { icon: CreditCard,  key: "step6", color: "bg-rose-500/10   text-rose-500"   },
]

const industries = [
  { icon: Ship,         key: "maritime",      color: "bg-blue-500/10   text-blue-600"   },
  { icon: Building2,    key: "construction",  color: "bg-amber-500/10  text-amber-600"  },
  { icon: Flame,        key: "energy",        color: "bg-orange-500/10 text-orange-600" },
  { icon: Heart,        key: "healthcare",    color: "bg-rose-500/10   text-rose-600"   },
  { icon: Leaf,         key: "agriculture",   color: "bg-green-500/10  text-green-600"  },
  { icon: Zap,          key: "manufacturing", color: "bg-yellow-500/10 text-yellow-600" },
  { icon: Cpu,          key: "technology",    color: "bg-violet-500/10 text-violet-600" },
  { icon: ShoppingBag,  key: "fmcg",          color: "bg-pink-500/10   text-pink-600"   },
  { icon: Mountain,     key: "mining",        color: "bg-stone-500/10  text-stone-600"  },
  { icon: Package,      key: "warehousing",   color: "bg-cyan-500/10   text-cyan-600"   },
  { icon: Car,          key: "automotive",    color: "bg-teal-500/10   text-teal-600"   },
  { icon: GraduationCap, key: "education",    color: "bg-indigo-500/10 text-indigo-600" },
]

const capabilities = [
  {
    icon: Search,
    key: "rfq",
    color: "bg-blue-500/10 text-blue-600",
    border: "hover:border-blue-500/30",
    features: ["f1", "f2", "f3", "f4"],
  },
  {
    icon: ClipboardList,
    key: "orders",
    color: "bg-violet-500/10 text-violet-600",
    border: "hover:border-violet-500/30",
    features: ["f1", "f2", "f3", "f4"],
  },
  {
    icon: Truck,
    key: "logistics",
    color: "bg-cyan-500/10 text-cyan-600",
    border: "hover:border-cyan-500/30",
    features: ["f1", "f2", "f3", "f4"],
  },
  {
    icon: Shield,
    key: "finance",
    color: "bg-emerald-500/10 text-emerald-600",
    border: "hover:border-emerald-500/30",
    features: ["f1", "f2", "f3", "f4"],
  },
]

const inventoryCards = [
  { icon: Bell,     key: "reorder",   color: "bg-amber-500/10  text-amber-600"  },
  { icon: BarChart3, key: "analytics", color: "bg-violet-500/10 text-violet-600" },
  { icon: BookOpen, key: "catalogue", color: "bg-cyan-500/10   text-cyan-600"   },
]

const trustCards = [
  { icon: ShieldCheck, key: "verified",  color: "bg-blue-500/10   text-blue-600"   },
  { icon: Lock,        key: "escrow",    color: "bg-emerald-500/10 text-emerald-600" },
  { icon: Shield,      key: "insurance", color: "bg-amber-500/10  text-amber-600"  },
  { icon: Scale,       key: "dispute",   color: "bg-rose-500/10   text-rose-600"   },
]

const supplierBenefits = [
  { icon: Globe,       key: "benefit1", color: "bg-blue-500/10   text-blue-600"   },
  { icon: Bell,        key: "benefit2", color: "bg-amber-500/10  text-amber-600"  },
  { icon: CreditCard,  key: "benefit3", color: "bg-emerald-500/10 text-emerald-600" },
  { icon: Star,        key: "benefit4", color: "bg-violet-500/10 text-violet-600" },
]

const platformLinks = ["Vessel Tracking", "e-BOL", "Insurance", "Legal Hub", "Arbitration", "Payments"]

// ─── Component ───────────────────────────────────────────────────────────────

export default function ProcurementPage() {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">

        {/* ── Hero ────────────────────────────────────────────────────── */}
        <section className="relative bg-slate-950 text-white py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&q=80"
              alt="Global procurement"
              className="w-full h-full object-cover opacity-15"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-linear-to-br from-slate-950 via-slate-950/95 to-cyan-950/40" />
          </div>

          <div className="relative container mx-auto px-4 text-center">
            <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/40 mb-6" variant="outline">
              {t("procurementpage.hero.badge")}
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-balance leading-tight max-w-4xl mx-auto">
              {t("procurementpage.hero.title")}
            </h1>
            <p className="text-cyan-400 font-medium text-lg mb-4">
              {t("procurementpage.hero.subtitle")}
            </p>
            <p className="text-slate-300 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
              {t("procurementpage.hero.description")}
            </p>
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <Link to="/register">
                <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-xl hover:shadow-cyan-500/30 transition-all hover:scale-105">
                  {t("procurementpage.hero.buyerCta")} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="border-cyan-500/40 text-white hover:bg-cyan-500/10 bg-transparent">
                  {t("procurementpage.hero.supplierCta")}
                </Button>
              </Link>
            </div>
            <p className="text-slate-400 text-sm">{t("procurementpage.hero.tagline")}</p>
          </div>
        </section>

        {/* ── Stats ───────────────────────────────────────────────────── */}
        <section className="py-12 bg-background border-b">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: "2,500+", key: "suppliers" },
                { value: "80+",    key: "countries"  },
                { value: "25+",    key: "industries" },
                { value: "$2B+",   key: "orders"     },
              ].map(({ value, key }) => (
                <div key={key} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-cyan-500">{value}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t(`procurementpage.stats.${key}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How It Works ────────────────────────────────────────────── */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("procurementpage.workflow.title")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t("procurementpage.workflow.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {workflowSteps.map(({ icon: Icon, key, color }, idx) => (
                <div key={key} className="relative">
                  <Card className="h-full glass border-2 hover:border-cyan-500/30 hover:shadow-lg transition-all duration-300">
                    <CardContent className="p-6 space-y-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="text-2xl font-bold text-muted-foreground/30 tabular-nums">
                          {t(`procurementpage.workflow.${key}.label`)}
                        </span>
                      </div>
                      <h3 className="font-semibold text-lg">
                        {t(`procurementpage.workflow.${key}.title`)}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {t(`procurementpage.workflow.${key}.description`)}
                      </p>
                    </CardContent>
                  </Card>
                  {/* connector dot for larger screens */}
                  {idx < workflowSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-6 z-10">
                      <ArrowRight className="h-5 w-5 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Industries ──────────────────────────────────────────────── */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("procurementpage.industries.title")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t("procurementpage.industries.description")}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
              {industries.map(({ icon: Icon, key, color }) => (
                <Card
                  key={key}
                  className="glass border-2 hover:border-cyan-500/30 hover:shadow-md transition-all duration-300 group cursor-pointer hover:scale-[1.03]"
                >
                  <CardContent className="p-4 flex flex-col items-center text-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color} group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="text-xs font-semibold leading-tight">
                      {t(`procurementpage.industries.${key}.title`)}
                    </p>
                    <p className="text-xs text-muted-foreground leading-tight hidden sm:block">
                      {t(`procurementpage.industries.${key}.description`)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── Platform Capabilities ───────────────────────────────────── */}
        <section className="py-20 bg-slate-950 text-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("procurementpage.capabilities.title")}
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
                {t("procurementpage.capabilities.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {capabilities.map(({ icon: Icon, key, color, border, features }) => (
                <Card
                  key={key}
                  className={`bg-white/5 border border-white/10 backdrop-blur-sm ${border} hover:shadow-xl transition-all duration-300`}
                >
                  <CardContent className="p-8 space-y-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${color}`}>
                      <Icon className="h-7 w-7" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1 text-white">
                        {t(`procurementpage.capabilities.${key}.title`)}
                      </h3>
                      <p className="text-sm text-slate-400 leading-relaxed">
                        {t(`procurementpage.capabilities.${key}.description`)}
                      </p>
                    </div>
                    <ul className="space-y-2 pt-2">
                      {features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-cyan-400 mt-0.5 shrink-0" />
                          <span className="text-sm text-slate-300">
                            {t(`procurementpage.capabilities.${key}.${f}`)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── Inventory & Spend Intelligence ──────────────────────────── */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("procurementpage.inventory.title")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t("procurementpage.inventory.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {inventoryCards.map(({ icon: Icon, key, color }) => (
                <Card key={key} className="glass border-2 hover:border-cyan-500/30 hover:shadow-lg transition-all duration-300 group text-center">
                  <CardContent className="p-8 space-y-4">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto ${color} group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="h-8 w-8" />
                    </div>
                    <h3 className="font-semibold text-lg">
                      {t(`procurementpage.inventory.${key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(`procurementpage.inventory.${key}.description`)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── Trust & Compliance ──────────────────────────────────────── */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                {t("procurementpage.trust.title")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                {t("procurementpage.trust.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {trustCards.map(({ icon: Icon, key, color }) => (
                <Card key={key} className="glass border-2 hover:border-cyan-500/30 hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-6 space-y-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color} group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold">
                      {t(`procurementpage.trust.${key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(`procurementpage.trust.${key}.description`)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── Supplier Section ────────────────────────────────────────── */}
        <section className="py-20 bg-linear-to-br from-cyan-950 via-slate-950 to-slate-950 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30" variant="outline">
                  For Suppliers
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-balance">
                  {t("procurementpage.supplier.title")}
                </h2>
                <p className="text-slate-300 leading-relaxed text-lg">
                  {t("procurementpage.supplier.description")}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Link to="/register">
                    <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white transition-all hover:scale-105">
                      {t("procurementpage.supplier.joinCta")} <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/about">
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 bg-transparent">
                      {t("procurementpage.supplier.learnMore")}
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {supplierBenefits.map(({ icon: Icon, key, color }) => (
                  <div key={key} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 space-y-2 hover:border-cyan-500/40 hover:bg-white/8 transition-all duration-300">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h4 className="font-semibold text-sm">
                      {t(`procurementpage.supplier.${key}.title`)}
                    </h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {t(`procurementpage.supplier.${key}.description`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Platform Integration Banner ─────────────────────────────── */}
        <section className="py-16 bg-muted/30 border-y">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="h-5 w-5 text-cyan-500" />
                  <span className="text-xs font-bold text-cyan-600 uppercase tracking-widest">
                    Fully Integrated Platform
                  </span>
                </div>
                <h3 className="text-2xl font-bold">Connected to Every Ontime Maritime Module</h3>
                <p className="text-muted-foreground leading-relaxed text-sm max-w-xl">
                  Procurement orders automatically sync with your Digital BOL, vessel tracking records, insurance policies, legal hub, and payment ledger — eliminating manual data entry across systems.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 justify-center md:justify-end">
                {platformLinks.map((item) => (
                  <Badge key={item} variant="secondary" className="py-1.5 px-3 text-sm">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 text-cyan-500" />
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Dual CTA ────────────────────────────────────────────────── */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {/* Buyer CTA */}
              <div className="relative overflow-hidden rounded-3xl bg-linear-to-br from-cyan-600 to-cyan-800 text-white p-10 space-y-5">
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/10 -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/4" />
                <div className="relative z-10 space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                    <ShoppingBag className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">
                    {t("procurementpage.cta.buyer.title")}
                  </h3>
                  <p className="text-cyan-100 leading-relaxed text-sm">
                    {t("procurementpage.cta.buyer.description")}
                  </p>
                  <Link to="/register">
                    <Button className="bg-white text-cyan-700 hover:bg-cyan-50 font-semibold transition-all hover:scale-105 mt-2">
                      {t("procurementpage.cta.buyer.button")} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Supplier CTA */}
              <div className="relative overflow-hidden rounded-3xl bg-linear-to-br from-slate-800 to-slate-950 text-white p-10 space-y-5 border border-white/10">
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-cyan-500/10 -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-cyan-500/5 translate-y-1/2 -translate-x-1/4" />
                <div className="relative z-10 space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-cyan-400" />
                  </div>
                  <h3 className="text-2xl font-bold">
                    {t("procurementpage.cta.supplier.title")}
                  </h3>
                  <p className="text-slate-300 leading-relaxed text-sm">
                    {t("procurementpage.cta.supplier.description")}
                  </p>
                  <Link to="/register">
                    <Button className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold transition-all hover:scale-105 mt-2">
                      {t("procurementpage.cta.supplier.button")} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  )
}
